import type { Namespace } from '../types';

// Sidebar/breadcrumb labels, keyed by the manifests' English label strings
// (`useNavLabel` looks labels up here and falls back to the string itself).
// Grow Rooms / Plumbing Rooms stay invariant (domain nouns, HTML precedent).
export const nav = {
  // R-346 — these 18 rendered in ENGLISH on a German console. `useNavLabel`
  // takes a plain `string` and falls back to the label, so a missing nav key is
  // silent — unlike `t()`, where the type system is the parity test.
  // ⚠ Six of them were the whole ITSM → Assets group.
  'All Assets': { de: 'Alle Assets', en: 'All Assets' },
  'Register / Intake': { de: 'Erfassen / Zugang', en: 'Register / Intake' },
  'Asset Classes': { de: 'Asset-Klassen', en: 'Asset Classes' },
  'Models (SKUs)': { de: 'Modelle (SKUs)', en: 'Models (SKUs)' },
  'QR Labels': { de: 'QR-Etiketten', en: 'QR Labels' },
  Unregistered: { de: 'Nicht erfasst', en: 'Unregistered' },
  Approvals: { de: 'Freigaben', en: 'Approvals' },
  'My Approvals': { de: 'Meine Freigaben', en: 'My Approvals' },
  'Approval Rules': { de: 'Freigaberegeln', en: 'Approval Rules' },
  Requests: { de: 'Anforderungen', en: 'Requests' },
  'Purchase Orders': { de: 'Bestellungen', en: 'Purchase Orders' },
  Calibration: { de: 'Kalibrierung', en: 'Calibration' },
  Firmware: { de: 'Firmware', en: 'Firmware' },
  'Device Catalog': { de: 'Gerätekatalog', en: 'Device Catalog' },
  'Fleet Inventory': { de: 'Flotten-Inventar', en: 'Fleet Inventory' },
  Sites: { de: 'Standorte', en: 'Sites' },
  Tasks: { de: 'Aufgaben', en: 'Tasks' },
  Timeline: { de: 'Zeitachse', en: 'Timeline' },
  HQ: { de: 'HQ', en: 'HQ' },
  'Fleet Overview': { de: 'Standort-Übersicht', en: 'Fleet Overview' },
  Notifications: { de: 'Benachrichtigungen', en: 'Notifications' },
  // Monitor: the cross-cutting watch surfaces (SOC, Cockpit) — distinct from Farm,
  // which is where you operate a specific room. 'Monitor' is the same word in German.
  Monitor: { de: 'Monitor', en: 'Monitor' },
  Setup: { de: 'Setup', en: 'Setup' },
  Farm: { de: 'Farm', en: 'Farm' },
  ITSM: { de: 'ITSM', en: 'ITSM' },
  'Asset Lifecycle': { de: 'Asset Lifecycle', en: 'Asset Lifecycle' },
  'Asset record': { de: 'Asset-Akte', en: 'Asset record' },
  Maintenance: { de: 'Maintenance', en: 'Maintenance' },
  'Incident record': { de: 'Störungsakte', en: 'Incident record' },
  'Climate Programs': { de: 'Klima-Programme', en: 'Climate Programs' },
  SOC: { de: 'SOC', en: 'SOC' },
  Cockpit: { de: 'Cockpit', en: 'Cockpit' },
  // 2026-08-30: this labels the PRODUCTION PLANNING group, which was renamed
  // to just "Production" when the old Production feature was removed. The key
  // already existed with the right German, so the rename translated itself.
  Production: { de: 'Produktion', en: 'Production' },
  Plans: { de: 'Anbaupläne', en: 'Plans' },
  // R-353 — the Production group's crop-alert leaf. "Warnungen" rather than
  // "Alarme": an alarm in this console is the SOC's, and a crop alert is not one.
  Alerts: { de: 'Warnungen', en: 'Alerts' },
  'Climate tunnel': { de: 'Klima-Tunnel', en: 'Climate tunnel' },
  Commerce: { de: 'Commerce', en: 'Commerce' },
  Warehouse: { de: 'Warehouse', en: 'Warehouse' },
  Procurement: { de: 'Procurement', en: 'Procurement' },
  Logistics: { de: 'Logistics', en: 'Logistics' },
  Sales: { de: 'Sales', en: 'Sales' },
  Administration: { de: 'Administration', en: 'Administration' },
  History: { de: 'Verlauf', en: 'History' },
  Account: { de: 'Konto', en: 'Account' },
  Mapper: { de: 'Mapper', en: 'Mapper' },
  'IoT Devices': { de: 'IoT-Geräte', en: 'IoT Devices' },
  Microcontrollers: { de: 'Mikrocontroller', en: 'Microcontrollers' },
  'Device History': { de: 'Geräteverlauf', en: 'Device History' },
  Sensors: { de: 'Sensoren', en: 'Sensors' },
  Actuators: { de: 'Aktoren', en: 'Actuators' },
  'IoT Control': { de: 'IoT-Steuerung', en: 'IoT Control' },
  Overview: { de: 'Übersicht', en: 'Overview' },
  'Grow Rooms': { de: 'Grow Rooms', en: 'Grow Rooms' },
  'Plumbing Rooms': { de: 'Plumbing Rooms', en: 'Plumbing Rooms' },
  Users: { de: 'Benutzer', en: 'Users' },
  Registration: { de: 'Registrierung', en: 'Registration' },
  'User Management': { de: 'Benutzerverwaltung', en: 'User Management' },
  'Access & Roles': { de: 'Zugriff & Rollen', en: 'Access & Roles' },
  'Audit Log': { de: 'Audit-Protokoll', en: 'Audit Log' },
  // W2 ITSM nav (R-133): ITIL terms (Incidents/Changes) stay invariant like
  // the tab labels; the rest translates.
  Assets: { de: 'Assets', en: 'Assets' },
  // The assets-group leaf. German takes the noun: a bare 'Alle' dangles, and
  // 'Register' is a false friend there (it means index / tab-set, not an asset
  // register — which is 'Anlagenregister').
  All: { de: 'Alle Assets', en: 'All' },
  // Breadcrumb + page term. Previously untranslated, so the German breadcrumb
  // read 'Asset Register' while the page header said 'Asset-Register'.
  'Asset Register': { de: 'Anlagenregister', en: 'Asset Register' },
  Stockroom: { de: 'Lager', en: 'Stockroom' },
  Transfers: { de: 'Transfers', en: 'Transfers' },
  Receiving: { de: 'Wareneingang', en: 'Receiving' },
  Scan: { de: 'Scannen', en: 'Scan' },
  'QR labels': { de: 'QR-Etiketten', en: 'QR labels' },
  Locations: { de: 'Standorte', en: 'Locations' },
  Configuration: { de: 'Konfiguration', en: 'Configuration' },
  Commons: { de: 'Stammdaten', en: 'Commons' },
  Incidents: { de: 'Incidents', en: 'Incidents' },
  Changes: { de: 'Changes', en: 'Changes' },
  'Work orders': { de: 'Work Orders', en: 'Work orders' },
  // Cloud-only Problems/KB nav — previously absent, so these rendered in English
  // on the German cloud console. Wording matches the page itself
  // (`problems.ts` pageTitle = 'Probleme & Wissen'); the invariant-ITIL note
  // above names Incidents/Changes specifically, and the app already translates
  // Probleme on that page.
  'Problems & KB': { de: 'Probleme & Wissen', en: 'Problems & KB' },
  Problems: { de: 'Probleme', en: 'Problems' },
  'Knowledge Base': { de: 'Wissensdatenbank', en: 'Knowledge Base' },
  'ITSM Commons': { de: 'ITSM-Stammdaten', en: 'ITSM Commons' },
  'Master Data': { de: 'Stammdaten', en: 'Master Data' },
  Cultivation: { de: 'Kultivierung', en: 'Cultivation' },
  // The cultivation-group leaves (R-204). Wording matches each screen's own tab
  // label so the sidebar and the page heading read the same. R-210 folded Crops,
  // Varieties and Categories into the one Catalogue leaf.
  Catalogue: { de: 'Katalog', en: 'Catalogue' },
  Profiles: { de: 'Profile', en: 'Profiles' },
  Capacity: { de: 'Kapazität', en: 'Capacity' },
  'Conflict flags': { de: 'Konflikt-Flags', en: 'Conflict flags' },
  Import: { de: 'Import', en: 'Import' },
  Settings: { de: 'Einstellungen', en: 'Settings' },
  'Complete Registration': { de: 'Registrierung abschließen', en: 'Complete Registration' },
  'Password Recovery': { de: 'Passwort-Wiederherstellung', en: 'Password Recovery' },
} as const satisfies Namespace;
